'use client';

import { useSearchParams } from 'next/navigation';
import { format, parse } from 'date-fns';
import React, { type ReactNode, useContext, createContext, useCallback, useEffect, useMemo } from 'react';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useCuriosityManager } from '@/app/common/CuriosityManagerProvider';
import { URLParameters } from '@/app/common/urlParameters';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useUrlSearchParams } from './UrlSearchParamsProvider';
import type { ClickParams } from '../search/Curiosity/Analytics/utils';
import { Analytics } from '../search/Curiosity/Analytics';
import { getMarket } from '../search/Curiosity/utils';
import { PlatformEnum } from '../search/Curiosity/Analytics/utils/PlatformEnum';

type CuriosityProviderProps = {
  children: ReactNode;
};

export type CuriosityContextParams = {
  sendCuriosityClickEvent: (
    eventData: ClickParams,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void;
  sendCuriosityShownEvent: (
    eventData: CuriosityShownEventOptionalFields,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void;
  sendCuriosityRoomShownEvent: (
    eventData: CuriosityRoomShownEventOptionalFields,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void;
  sendCuriosityRoomRedirectEvent: (
    eventData: CuriosityRoomRedirectEventOptionalFields,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void;
  sendCuriosityFlightShownEvent: (
    eventData: CuriosityFlightShownEventOptionalFields,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void;
  sendCuriosityFlightRedirectEvent: (
    eventData: CuriosityFlightRedirectEventOptionalFields,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void;
  sendCuriositySummaryShownEvent: (
    eventData: CuriositySummaryShownEventOptionalFields,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void;
  sendCuriositySummaryRedirectEvent: (
    eventData: CuriositySummaryRedirectEventOptionalFields,
    options?: { finallyCallback?: () => void; onlyOnce?: boolean }
  ) => void;
};

const CuriosityContext = createContext<CuriosityContextParams | undefined>(undefined);

// This is to avoid sending several times some events when coming back from flights page
const onlyOnceEventSent: string[] = [];

export function CuriosityProvider(props: CuriosityProviderProps) {
  const { children } = props;
  const { internalHotelId } = useInitialParams();
  const { staticData, commonData, localization, curiosityParams } = usePageWrapperData();

  const { locale } = localization;
  const userId = commonData.userUid;
  const { sessionId, deviceType } = commonData;
  const { checkin, checkout, adults, accommodationOnly } = useUrlSearchParams();
  const searchParams = useSearchParams();

  const { curiosityClickEvents } = useCuriosityManager();

  useEffect(() => {
    if (!userId || !sessionId) return;
    const onUnload = () => {
      if (!userId || !sessionId) return;
      // eslint-disable-next-line consistent-return
      return sendCuriosityClickEvent({
        event_name: 'event_page_leave',
      });
    };
    window.addEventListener('beforeunload', onUnload);
    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('beforeunload', onUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, sessionId, accommodationOnly, locale, deviceType]);

  const sendCuriosityClickEvent = useCallback(
    (eventData: ClickParams, options?: { onlyOnce?: boolean }) => {
      if (options?.onlyOnce && onlyOnceEventSent.includes(eventData.event_name)) {
        return;
      }

      curiosityClickEvents.hotelDetailsClick({
        name: eventData.event_name,
        context: eventData.event_context,
        subContext: eventData.event_subcontext,
        value: eventData.event_value,
        additionalValue: eventData.event_value_additional,
      });

      if (options?.onlyOnce) {
        onlyOnceEventSent.push(eventData.event_name);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, sessionId, accommodationOnly, staticData.domainValues?.brand, locale, deviceType]
  );

  const sendCuriosityShownEvent = useCallback(
    (eventData: CuriosityShownEventOptionalFields) => {
      const event: CuriosityShownEvent = {
        ...eventData,
        type: 'sdp_tsx_shown_dp',
        people_adults: adults,
        hotel_id: internalHotelId,
        checkin_date:
          eventData.checkin_date && eventData.checkin_date !== ''
            ? eventData.checkin_date
            : format(parse(checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
        checkout_date:
          eventData.checkout_date && eventData.checkout_date !== ''
            ? eventData.checkout_date
            : format(parse(checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
      };

      const { brand, country } = localization;
      const { isApp } = commonData;
      const platform = isApp ? PlatformEnum.APP : PlatformEnum.TSX;

      const analytics = new Analytics({
        market: getMarket(country),
        platform,
        brand,
        searchId: searchParams.get(URLParameters.VC_SEARCH_ID) ?? undefined,
        isServer: false,
        commonData,
        curiosityParams,
        localization,
        AbortController,
      });

      analytics.sendDetailsEvent(event);
    },
    [adults, internalHotelId, checkin, checkout, searchParams]
  );

  const sendCuriosityRoomShownEvent = useCallback(
    (eventData: CuriosityRoomShownEventOptionalFields) => {
      const event: CuriosityRoomShownEvent = {
        ...eventData,
        type: 'sdp_tsx_room_shown_dp',
        people_adults: adults,
        hotel_id: internalHotelId,
        checkin_date:
          eventData.checkin_date && eventData.checkin_date !== ''
            ? eventData.checkin_date
            : format(parse(checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
        checkout_date:
          eventData.checkout_date && eventData.checkout_date !== ''
            ? eventData.checkout_date
            : format(parse(checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
      };

      const { brand, country } = localization;
      const { isApp } = commonData;
      const platform = isApp ? PlatformEnum.APP : PlatformEnum.TSX;

      const analytics = new Analytics({
        market: getMarket(country),
        platform,
        brand,
        searchId: searchParams.get(URLParameters.VC_SEARCH_ID) ?? undefined,
        isServer: false,
        commonData,
        curiosityParams,
        localization,
        AbortController,
      });

      analytics.sendDetailsEvent(event);
    },
    [adults, internalHotelId, checkin, checkout, searchParams]
  );

  const sendCuriosityRoomRedirectEvent = useCallback(
    (eventData: CuriosityRoomRedirectEventOptionalFields) => {
      const event: CuriosityRoomRedirectEvent = {
        ...eventData,
        type: 'sdp_tsx_room_redirect_dp',
        people_adults: adults,
        hotel_id: internalHotelId,
        checkin_date:
          eventData.checkin_date && eventData.checkin_date !== ''
            ? eventData.checkin_date
            : format(parse(checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
        checkout_date:
          eventData.checkout_date && eventData.checkout_date !== ''
            ? eventData.checkout_date
            : format(parse(checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
      };
      const { brand, country } = localization;
      const { isApp } = commonData;
      const platform = isApp ? PlatformEnum.APP : PlatformEnum.TSX;

      const analytics = new Analytics({
        market: getMarket(country),
        platform,
        brand,
        searchId: searchParams.get(URLParameters.VC_SEARCH_ID) ?? undefined,
        isServer: false,
        commonData,
        curiosityParams,
        localization,
        AbortController,
      });

      analytics.sendDetailsEvent(event);
    },
    [adults, checkin, checkout, internalHotelId, searchParams]
  );

  const sendCuriosityFlightShownEvent = useCallback(
    (eventData: CuriosityFlightShownEventOptionalFields) => {
      const event: CuriosityFlightShownEvent = {
        ...eventData,
        type: 'sdp_tsx_flight_shown_dp',
        people_adults: adults,
        hotel_id: internalHotelId,
        checkin_date:
          eventData.checkin_date && eventData.checkin_date !== ''
            ? eventData.checkin_date
            : format(parse(checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
        checkout_date:
          eventData.checkout_date && eventData.checkout_date !== ''
            ? eventData.checkout_date
            : format(parse(checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
      };

      const { brand, country } = localization;
      const { isApp } = commonData;
      const platform = isApp ? PlatformEnum.APP : PlatformEnum.TSX;

      const analytics = new Analytics({
        market: getMarket(country),
        platform,
        brand,
        searchId: searchParams.get(URLParameters.VC_SEARCH_ID) ?? '',
        isServer: false,
        commonData,
        curiosityParams,
        localization,
        AbortController,
      });

      analytics.sendDetailsEvent(event);
    },
    [adults, internalHotelId, checkin, checkout, searchParams]
  );

  const sendCuriosityFlightRedirectEvent = useCallback(
    (eventData: CuriosityFlightRedirectEventOptionalFields) => {
      const event: CuriosityFlightRedirectEvent = {
        ...eventData,
        type: 'sdp_tsx_flight_redirect_dp',
        people_adults: adults,
        hotel_id: internalHotelId,
        checkin_date:
          eventData.checkin_date && eventData.checkin_date !== ''
            ? eventData.checkin_date
            : format(parse(checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
        checkout_date:
          eventData.checkout_date && eventData.checkout_date !== ''
            ? eventData.checkout_date
            : format(parse(checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
      };
      const { brand, country } = localization;
      const { isApp } = commonData;
      const platform = isApp ? PlatformEnum.APP : PlatformEnum.TSX;

      const analytics = new Analytics({
        market: getMarket(country),
        platform,
        brand,
        searchId: searchParams.get(URLParameters.VC_SEARCH_ID) ?? undefined,
        isServer: false,
        commonData,
        curiosityParams,
        localization,
        AbortController,
      });

      analytics.sendDetailsEvent(event);
    },
    [adults, checkin, checkout, internalHotelId, searchParams]
  );

  const sendCuriositySummaryShownEvent = useCallback(
    (eventData: CuriositySummaryShownEventOptionalFields) => {
      const event: CuriositySummaryShownEvent = {
        ...eventData,
        type: 'sdp_tsx_summary_shown_dp_type',
        people_adults: adults,
        hotel_id: internalHotelId,
        checkin_date:
          eventData.checkin_date && eventData.checkin_date !== ''
            ? eventData.checkin_date
            : format(parse(checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
        checkout_date:
          eventData.checkout_date && eventData.checkout_date !== ''
            ? eventData.checkout_date
            : format(parse(checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
      };

      const { brand, country } = localization;
      const { isApp } = commonData;
      const platform = isApp ? PlatformEnum.APP : PlatformEnum.TSX;

      const analytics = new Analytics({
        market: getMarket(country),
        platform,
        brand,
        searchId: searchParams.get(URLParameters.VC_SEARCH_ID) ?? undefined,
        isServer: false,
        commonData,
        curiosityParams,
        localization,
        AbortController,
      });

      analytics.sendDetailsEvent(event);
    },
    [adults, internalHotelId, checkin, checkout, searchParams]
  );

  const sendCuriositySummaryRedirectEvent = useCallback(
    async (eventData: CuriositySummaryRedirectEventOptionalFields, options?: { finallyCallback?: () => void }) => {
      const event: CuriositySummaryRedirectEvent = {
        ...eventData,
        type: 'sdp_tsx_summary_redirect_dp_type',
        people_adults: adults,
        hotel_id: internalHotelId,
        checkin_date:
          eventData.checkin_date && eventData.checkin_date !== ''
            ? eventData.checkin_date
            : format(parse(checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
        checkout_date:
          eventData.checkout_date && eventData.checkout_date !== ''
            ? eventData.checkout_date
            : format(parse(checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd'), // Format YYYY-MM-DD
      };
      const { brand, country } = localization;
      const { isApp } = commonData;
      const platform = isApp ? PlatformEnum.APP : PlatformEnum.TSX;

      const analytics = new Analytics({
        market: getMarket(country),
        platform,
        brand,
        searchId: searchParams.get(URLParameters.VC_SEARCH_ID) ?? undefined,
        isServer: false,
        commonData,
        curiosityParams,
        localization,
        AbortController,
      });

      analytics.sendAsyncDetailsEvent(event).finally(() => {
        if (options?.finallyCallback) {
          options.finallyCallback();
        }
      });
    },
    [adults, checkin, checkout, internalHotelId, searchParams]
  );

  const memoizedValue = useMemo(
    () => ({
      sendCuriosityClickEvent,
      sendCuriosityShownEvent,
      sendCuriosityRoomShownEvent,
      sendCuriosityRoomRedirectEvent,
      sendCuriosityFlightShownEvent,
      sendCuriosityFlightRedirectEvent,
      sendCuriositySummaryShownEvent,
      sendCuriositySummaryRedirectEvent,
    }),
    [
      sendCuriosityClickEvent,
      sendCuriosityShownEvent,
      sendCuriosityRoomShownEvent,
      sendCuriosityRoomRedirectEvent,
      sendCuriosityFlightShownEvent,
      sendCuriosityFlightRedirectEvent,
      sendCuriositySummaryShownEvent,
      sendCuriositySummaryRedirectEvent,
    ]
  );

  return <CuriosityContext.Provider value={memoizedValue}> {children} </CuriosityContext.Provider>;
}

export function useCuriosity() {
  const context = useContext(CuriosityContext);

  if (context === undefined) {
    return {
      sendCuriosityClickEvent: () => {},
      sendCuriosityShownEvent: () => {},
      sendCuriosityRoomShownEvent: () => {},
      sendCuriosityRoomRedirectEvent: () => {},
      sendCuriosityFlightShownEvent: () => {},
      sendCuriosityFlightRedirectEvent: () => {},
      sendCuriositySummaryShownEvent: () => {},
      sendCuriositySummaryRedirectEvent: () => {},
    };
    // throw new Error('useCuriosity must be within a CuriosityContext');
  }

  return context;
}
